import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Pencil, Archive, CheckCircle2, Circle, PauseCircle } from "lucide-react";
import { toast } from "sonner";
import { Card, Spinner } from "@/components/common";
import WPStatusBadge, { WPScopeNote } from "./WPStatusBadge";
import { WPLogoPreview, WPLogoPicker } from "./WPLogoField";
import WPHistory from "./WPHistory";
import { wpApi, btnPrimary, btnGhost, useWP } from "./wpApi";
import { packageT } from "./packageText";

const REQ = ["company", "website_url", "logo", "goal", "languages", "page_scope"];

function Checklist({ order }) {
  const { t, field } = useWP();
  return (
    <Card className="p-5 sm:p-6" data-testid="wp-checklist">
      <h2 className="text-sm font-bold uppercase tracking-[0.16em] text-gray-400 mb-3">{t("Pflichtangaben")}</h2>
      <ul className="grid sm:grid-cols-2 gap-2">
        {REQ.map((k) => {
          // Archived orders intentionally have missing=[] for workflow actions.
          // The checklist still describes the fields actually stored.
          const ok = Array.isArray(order[k]) ? order[k].length > 0 : Boolean(order[k]);
          return (
            <li key={k} className="flex items-center gap-2 text-sm" data-testid={`wp-check-${k}`} data-ok={ok}>
              {ok ? <CheckCircle2 className="w-4 h-4 text-[#A3E635]" /> : <Circle className="w-4 h-4 text-gray-600" />}
              <span className={ok ? "text-gray-200" : "text-gray-500"}>{field(k)}</span>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

function Facts({ order }) {
  const { t, field, fmtValue } = useWP();
  const rows = ["website_url", "page_scope", "languages", "goal", "notes"];
  return (
    <Card className="p-5 sm:p-6" data-testid="wp-facts">
      <h2 className="text-sm font-bold uppercase tracking-[0.16em] text-gray-400 mb-3">{t("Angaben")}</h2>
      <dl className="space-y-3 text-sm">
        {rows.map((k) => (
          <div key={k} className="grid grid-cols-[140px_1fr] gap-3">
            <dt className="text-gray-500">{field(k)}</dt>
            <dd className="text-gray-200 break-words whitespace-pre-line" data-testid={`wp-fact-${k}`}>{fmtValue(k, order[k])}</dd>
          </div>
        ))}
        {order.preset_name && <div className="grid grid-cols-[140px_1fr] gap-3"><dt className="text-gray-500">{t("Vorlage")}</dt><dd className="text-gray-200">{order.preset_name}</dd></div>}
      </dl>
    </Card>
  );
}

export default function WPOrderDetail() {
  const { id } = useParams();
  const { t, err, fmtDate, lang } = useWP();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [logo, setLogo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);

  const load = useCallback(() => wpApi.get(id).then((r) => { setData(r.data); setError(null); })
    .catch((e) => setError(e)), [id]);
  useEffect(() => { load(); }, [load]);

  if (error && !data) return <Card className="p-8" data-testid="wp-detail-error"><p className="text-red-300">{err(error, "Auftrag nicht gefunden")}</p></Card>;
  if (!data) return <Spinner />;
  const o = data.order;
  const archived = o.status === "archiviert";

  const run = async (fn, ok, fail) => {
    setBusy(true);
    try { await fn(); toast.success(t(ok)); await load(); } catch (e) { toast.error(err(e, fail)); } finally { setBusy(false); }
  };
  const uploadLogo = () => run(async () => { await wpApi.uploadLogo(id, logo, o.revision); setLogo(null); }, "Logo gespeichert", "Logo wurde nicht gespeichert");
  const archive = () => run(async () => { await wpApi.archive(id, o.revision); setConfirm(false); }, "Auftrag archiviert", "Archivieren fehlgeschlagen");

  return (
    <div data-testid="wp-detail-page" className="max-w-5xl">
      <Link to="/admin/website-production" className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-white mb-4 transition-colors" data-testid="wp-detail-back"><ArrowLeft className="w-4 h-4" /> {t("Alle Aufträge")}</Link>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div className="min-w-0">
          <h1 className="text-3xl sm:text-4xl font-black tracking-tighter text-white break-words" data-testid="wp-detail-company">{o.company}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-500">
            <WPStatusBadge status={o.status} testid="wp-detail-status" />
            <span className="font-mono" data-testid="wp-detail-revision">{t("Version")} {o.revision}</span>
            <span>{t("Aktualisiert")} {fmtDate(o.updated_at)}</span>
          </div>
        </div>
        {!archived && (
          <div className="flex flex-wrap gap-2">
            <Link to={`/admin/website-production/${id}/package`} className={btnPrimary} data-testid="wp-client-package-btn">{packageT(lang, "Kundenpaket", t)}</Link>
            <Link to={`/admin/website-production/${id}/edit`} className={btnGhost} data-testid="wp-edit-btn"><Pencil className="w-4 h-4" /> {t("Bearbeiten")}</Link>
            {confirm ? (
              <>
                <button onClick={archive} disabled={busy} className={btnPrimary} data-testid="wp-archive-confirm">{t("Ja, archivieren")}</button>
                <button onClick={() => setConfirm(false)} className={btnGhost} data-testid="wp-archive-cancel">{t("Nein")}</button>
              </>
            ) : <button onClick={() => setConfirm(true)} className={btnGhost} data-testid="wp-archive-btn"><Archive className="w-4 h-4" /> {t("Archivieren")}</button>}
          </div>
        )}
      </div>
      <WPScopeNote />
      <div className="grid lg:grid-cols-[1fr_1fr] gap-5">
        <div className="space-y-5 min-w-0">
          <Checklist order={o} />
          <Facts order={o} />
          <Card className="p-5 sm:p-6 space-y-4" data-testid="wp-logo-section">
            <h2 className="text-sm font-bold uppercase tracking-[0.16em] text-gray-400">{t("Original-Logo")}</h2>
            <WPLogoPreview orderId={id} logo={o.logo} />
            {!archived && (
              <div className="space-y-3 pt-2 border-t border-white/5">
                <WPLogoPicker file={logo} onChange={setLogo} testid="wp-logo-replace" />
                {logo && <button onClick={uploadLogo} disabled={busy} className={btnPrimary} data-testid="wp-logo-upload-btn">{t(o.logo ? "Logo ersetzen" : "Logo speichern")}</button>}
              </div>
            )}
          </Card>
          <Card className="p-5 sm:p-6 flex items-start gap-3" data-testid="wp-run-status">
            <PauseCircle className="w-5 h-5 text-gray-500 shrink-0 mt-0.5" />
            <div><div className="text-sm font-semibold text-white">{t("Produktionslauf: nicht gestartet")}</div><p className="text-xs text-gray-500 mt-0.5">{t("Die Steuerung der neun Phasen folgt in Abschnitt 1b.")}</p></div>
          </Card>
        </div>
        <WPHistory events={data.events} />
      </div>
    </div>
  );
}


